import { useEffect, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";

const CLEANUP_FREQS = ["weekly","biweekly","twice-weekly"];
const DOG_COUNTS    = [1,2,3,4];

export function PricingEditor({ showToast, onRefresh, clients }) {
  const isMobile = useIsMobile();
  const [prices,       setPrices]       = useState(null);
  const [origPrices,   setOrigPrices]   = useState(null); // snapshot before edits
  const [deo,          setDeo]          = useState(null);
  const [origDeo,      setOrigDeo]      = useState(null); // snapshot before edits (L2)
  const [saving,       setSaving]       = useState(false);
  const [dirty,        setDirty]        = useState(false);
  const [changed,      setChanged]      = useState([]); // [{freq,dogs}] combos that changed
  // eslint-disable-next-line no-unused-vars
  const [showStripe,   setShowStripe]   = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [applyingStripe, setApplyingStripe] = useState(false);

  useEffect(()=>{
    fetch(`${SUPABASE_URL}/rest/v1/service_prices`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}})
      .then(r=>r.json()).then(rows=>{
        if (!Array.isArray(rows)) return;
        const m = {};
        const d = {};
        rows.forEach((r:any)=>{
          if (r.service==="cleanup") {
            if (!m[r.frequency]) m[r.frequency]={};
            m[r.frequency][r.dogs] = Number(r.price);
          } else if (r.service==="deo") {
            d[r.frequency] = Number(r.price);
          }
        });
        setPrices(m);
        setOrigPrices(JSON.parse(JSON.stringify(m))); // deep copy snapshot
        setDeo(d);
        setOrigDeo(JSON.parse(JSON.stringify(d)));     // deo baseline (L2)
      });
  },[]);

  async function savePrices() {
    setSaving(true);
    try {
      // 1. Save all prices to DB. Upsert (not just PATCH) so a brand-new
      //    operator with no rows yet gets them inserted. Keyed on the per-org
      //    unique index (organization_id auto-stamped on insert).
      const upsertPrice = (body) => fetch(
        `${SUPABASE_URL}/rest/v1/service_prices?on_conflict=organization_id,service,frequency,dogs`,{
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"resolution=merge-duplicates,return=minimal"},
          body:JSON.stringify(body),
        });
      for (const freq of ["weekly","biweekly","twice-weekly"]) {
        for (const dogs of [1,2,3,4]) {
          const val = prices?.[freq]?.[dogs];
          if (!val) continue;
          await upsertPrice({ service:"cleanup", frequency:freq, dogs, price:val });
        }
      }
      for (const freq of ["weekly","biweekly","monthly"]) {
        const val = deo?.[freq];
        if (!val) continue;
        await upsertPrice({ service:"deo", frequency:freq, dogs:null, price:val });
      }

      // 2. Update Stripe subscriptions for affected clients (cleanup AND deo — L2).
      const deoChangedFreqs = ["weekly","biweekly","monthly"].filter(f =>
        Number(deo?.[f]||0) !== Number(origDeo?.[f]||0));
      const cleanupAffected = (clients||[]).filter(c=>c.active&&c.stripe_subscription_id&&
        changed.some(ch=>ch.freq===c.frequency&&Number(ch.dogs)===Number(c.dogs)));
      // 067: a merged client's deo is an ITEM on the main sub (deo_stripe_item_id),
      // not its own subscription — both shapes are repriceable server-side.
      const deoAffected = (clients||[]).filter(c=>c.active&&(c.deo_stripe_subscription_id||c.deo_stripe_item_id)&&
        deoChangedFreqs.includes(c.deo_frequency));
      const affectedIds = [...new Set([...cleanupAffected, ...deoAffected].map(c=>c.id))];

      // Only push POSITIVE prices to Stripe. update-subscription-prices runs
      // validAmount() (rejects <= 0) and 400s the whole call on a $0 entry, which
      // we'd otherwise mis-report as success. A $0 price stays CRM-only; removing
      // a deo plan should be done by cancelling the deo sub, not repricing to $0.
      const pricingChanges = changed
        .map(ch=>({ frequency:ch.freq, dogs:Number(ch.dogs), new_price: prices?.[ch.freq]?.[Number(ch.dogs)] }))
        .filter(c=>Number(c.new_price) > 0);
      const deoPricingChanges = deoChangedFreqs
        .map(f=>({ deo_frequency:f, new_price: deo?.[f] }))
        .filter(c=>Number(c.new_price) > 0);
      const zeroedDeo = deoChangedFreqs.some(f => !(Number(deo?.[f]) > 0));

      if (affectedIds.length > 0 && (pricingChanges.length || deoPricingChanges.length)) {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/update-subscription-prices`,{
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
          body:JSON.stringify({
            client_ids: affectedIds,
            pricing_changes: pricingChanges,
            deo_pricing_changes: deoPricingChanges,
          })
        }).then(r=>r.json());
        if (res.error) showToast(`Stripe sync failed — prices saved to CRM only: ${res.error}`, "error");
        else if (res.errors?.length > 0) showToast(`Stripe errors: ${res.errors.join(", ")}`, "error");
        else if (res.updated > 0) showToast(`Prices saved + ${res.updated} Stripe subscription${res.updated!==1?"s":""} updated`);
        else showToast("Prices saved to CRM");
      } else if (zeroedDeo) {
        showToast("Prices saved to CRM — a $0 deodorizer price was not pushed to Stripe (cancel the deo plan instead).", "error");
      } else {
        showToast("Prices saved");
      }

      setDirty(false);
      setChanged([]);
      setOrigPrices(JSON.parse(JSON.stringify(prices)));
      setOrigDeo(JSON.parse(JSON.stringify(deo || {})));
      onRefresh && onRefresh();
    } catch(e) { showToast("Error saving prices: "+(e.message||e),"error"); }
    setSaving(false);
  }

  // One cleanup cell edited (shared by the desktop table and the phone stack).
  function setCleanupPrice(freq, dogs, raw) {
    const newVal = Math.max(0, Number(raw) || 0);
    setPrices(p=>({...p,[freq]:{...(p?.[freq]||{}),[dogs]:newVal}}));
    setDirty(true);
    // Track if this combo actually changed from original
    setChanged(prev=>{
      const key = `${freq}-${dogs}`;
      const origVal = origPrices?.[freq]?.[dogs];
      const isChanged = Math.abs(newVal - (origVal||0)) > 0.001;
      const without = prev.filter(c=>c.key!==key);
      return isChanged ? [...without,{freq,dogs,key}] : without;
    });
  }

  if (!prices) return <div style={{padding:20,color:"var(--text2)",fontSize:12}}>Loading prices...</div>;

  return (
    <div style={{marginTop:20}}>
      <div className="grid-2">
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div className="card-title" style={{marginBottom:0}}>🧹 Cleanup Pricing (Monthly)</div>
            {dirty&&<span style={{fontSize:11,color:"#974f0c",background:"#fffae6",padding:"2px 8px",borderRadius:4}}>Unsaved changes</span>}
          </div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:12}}>Changes apply to new invoices only — existing subscriptions update on their next billing cycle.</div>
          {isMobile ? (
            // Phones: the 5-column table needs ~470px and the 3-/4-dog cells were clipped
            // off-screen (unreachable). Stack one block per frequency, four labelled inputs
            // in a 2×2 grid, each stretching to its cell.
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {CLEANUP_FREQS.map(freq=>(
                <div key={freq} style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                  <div style={{fontSize:13,fontWeight:600,textTransform:"capitalize",color:"var(--text)",marginBottom:8}}>{freq}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8}}>
                    {DOG_COUNTS.map(dogs=>(
                      <label key={dogs} style={{display:"flex",flexDirection:"column",gap:4,minWidth:0}}>
                        <span style={{fontSize:11,color:"var(--text2)"}}>{dogs} dog{dogs>1?"s":""}</span>
                        <span style={{display:"flex",alignItems:"center",background:"var(--bg)",borderRadius:8,padding:"6px 10px",minHeight:44}}>
                          <span style={{fontSize:13,color:"var(--text2)",marginRight:3}}>$</span>
                          <input type="number" step="0.01" inputMode="decimal" value={(prices?.[freq]?.[dogs]||0).toFixed(2)}
                            onFocus={e=>e.target.select()}
                            style={{width:"100%",minWidth:0,border:"none",background:"transparent",fontWeight:600,color:NAVY,outline:"none"}}
                            onChange={e=>setCleanupPrice(freq,dogs,e.target.value)}
                          />
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>
                <th style={{fontSize:11,color:"var(--text2)",padding:"6px 8px",textAlign:"left"}}>Frequency</th>
                {DOG_COUNTS.map(d=><th key={d} style={{fontSize:11,color:"var(--text2)",padding:"6px 8px",textAlign:"center"}}>{d} dog{d>1?"s":""}</th>)}
              </tr>
            </thead>
            <tbody>
              {CLEANUP_FREQS.map(freq=>(
                <tr key={freq} style={{borderTop:"1px solid #f0f2f5"}}>
                  <td style={{padding:"8px",fontSize:12,fontWeight:600,textTransform:"capitalize"}}>{freq}</td>
                  {DOG_COUNTS.map(dogs=>(
                    <td key={dogs} style={{padding:"4px 2px"}}>
                      <div style={{display:"flex",alignItems:"center",background:"var(--bg)",borderRadius:6,padding:"4px 8px"}}>
                        <span style={{fontSize:12,color:"var(--text2)",marginRight:2}}>$</span>
                        <input type="number" step="0.01" value={(prices?.[freq]?.[dogs]||0).toFixed(2)}
                          style={{width:70,border:"none",background:"transparent",fontSize:12,fontWeight:600,color:NAVY,outline:"none"}}
                          onChange={e=>setCleanupPrice(freq,dogs,e.target.value)}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        <div className="card">
          <div className="card-title">💨 Deodorizer Pricing (Per Visit)</div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:12}}>Per visit, regardless of dog count.</div>
          {[
            {freq:"weekly",   label:"Weekly"},
            {freq:"biweekly", label:"Bi-Weekly"},
            {freq:"monthly",  label:"Monthly"},
          ].map(({freq,label})=>(
            <div key={freq} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 0",borderBottom:"1px solid #f0f2f5"}}>
              <div style={{fontWeight:600,fontSize:13}}>{label}</div>
              <div style={{display:"flex",alignItems:"center",background:"var(--bg)",borderRadius:6,padding:"6px 10px",minHeight:isMobile?44:undefined}}>
                <span style={{fontSize:12,color:"var(--text2)",marginRight:2}}>$</span>
                <input type="number" step="0.01" inputMode="decimal" value={(deo?.[freq]||0).toFixed(2)}
                  onFocus={isMobile ? (e=>e.target.select()) : undefined}
                  style={{width:isMobile?96:80,border:"none",background:"transparent",fontSize:14,fontWeight:700,color:NAVY,outline:"none"}}
                  onChange={e=>{ setDeo(d=>({...d,[freq]:Math.max(0, Number(e.target.value) || 0)})); setDirty(true); }}
                />
                <span style={{fontSize:11,color:"var(--text2)"}}>/visit</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",marginTop:12,gap:10,flexWrap:"wrap"}}>
        {/* Phones: a faded disabled button doesn't read as disabled — say why it's off. */}
        {isMobile && !dirty && !saving && <span style={{fontSize:11.5,color:"var(--text2)"}}>Change a price to enable.</span>}
        <button className="btn btn-gold" onClick={savePrices} disabled={saving||!dirty} style={isMobile?{flex:"1 1 auto"}:undefined}>
          {saving?"Saving...":"✓ Apply Pricing Changes"}
        </button>
      </div>

    </div>
  );
}
